import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table } from 'antd';
import useDatasource from '../../hooks/useDatasource';
import { FormContext } from '../../hooks/useForm';
import Column from '../../helpers/columns';
import { getStations } from '../../services/taxonomies/stations';
import FormWrapper from '../FormWrapper';

const StationsList = ({ canEdit = true }) => {
  const form = useContext(FormContext);
  const { t } = useTranslation();

  const { value, setValue, errorMessages } = form;

  const stationsMeta = useMemo(
    () => value.stationsMeta || [],
    [value.stationsMeta],
  );

  // Keep 'stations' list in sync with unique stationIds from stationsMeta
  useEffect(() => {
    const uniqueStationIds = Array.from(
      new Set(stationsMeta.map((s) => s.stationId)),
    );
    setValue('stations', uniqueStationIds);
  }, [stationsMeta, setValue]);

  const makeRouteStation = useCallback(
    (stationId, direction) => ({
      stationId,
      direction,
      position: stationsMeta.length, // Temporary, will be re-ordered later
    }),
    [stationsMeta.length],
  );

  const handleChange = useCallback(
    (stationId, direction, checked) => {
      const filtered = stationsMeta.filter(
        (s) => !(s.stationId === stationId && s.direction === direction),
      );
      setValue(
        'stationsMeta',
        checked
          ? [...filtered, makeRouteStation(stationId, direction)]
          : filtered,
      );
    },
    [stationsMeta, makeRouteStation, setValue],
  );

  const columns = useMemo(
    () => [
      {
        title: t('entity.taxonomy.station.tur'),
        key: 'tur',
        width: 80,
        render: (_, record) => (
          <Checkbox
            disabled={!canEdit}
            checked={stationsMeta.some(
              (s) => s.stationId === record.id && s.direction === 'TUR',
            )}
            onChange={(e) => handleChange(record.id, 'TUR', e.target.checked)}
          />
        ),
      },
      {
        title: t('entity.taxonomy.station.retur'),
        key: 'retur',
        width: 80,
        render: (_, record) => (
          <Checkbox
            disabled={!canEdit}
            checked={stationsMeta.some(
              (s) => s.stationId === record.id && s.direction === 'RETUR',
            )}
            onChange={(e) => handleChange(record.id, 'RETUR', e.target.checked)}
          />
        ),
      },
      Column.text('name', t('entity.taxonomy.station.name'), {
        filter: true,
      }),
      Column.bool('deleted', t('entity.taxonomy.station.deleted._'), {
        width: 150,
        filter: true,
        inverted: true,
        labels: [
          t('table.filter.all'),
          t('entity.taxonomy.station.deleted.true'),
          t('entity.taxonomy.station.deleted.false'),
          t('table.filter.unknown'),
        ],
      }),
    ],
    [t, stationsMeta, handleChange, canEdit],
  );

  const {
    pagination,
    content,
    handleChange: handleTableChange,
  } = useDatasource(getStations);

  return (
    <>
      <FormWrapper.ErrorMessage message={errorMessages?.stationsMeta} />
      <Table
        columns={columns}
        rowKey="id"
        pagination={pagination}
        dataSource={content}
        onChange={handleTableChange}
      />
    </>
  );
};

export default StationsList;
